<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

interface Props {
  show: boolean
}

interface Emits {
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const { t } = useI18n()

// Component props

const countdown = ref(30)
let timer: NodeJS.Timeout | null = null

const handleOk = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
  emit('close')
}

const handleDialogUpdate = (value: boolean) => {
  if (!value)
    emit('close')
}

const startCountdown = () => {
  countdown.value = 30
  timer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      if (timer) {
        clearInterval(timer)
        timer = null
      }
      emit('close')
    }
  }, 1000)
}

const stopCountdown = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

onMounted(() => {
  if (props.show)
    startCountdown()
})

onUnmounted(() => {
  stopCountdown()
})

// Watch for show prop changes
watch(() => props.show, newShow => {
  if (newShow)
    startCountdown()
  else
    stopCountdown()
})
</script>

<template>
  <VDialog
    :model-value="show"
    max-width="400"
    persistent
    class="z-[9999]"
    @update:model-value="handleDialogUpdate"
  >
    <VCard class="text-center">
      <!-- Icon -->
      <VCardText class="pt-6">
        <VAvatar
          size="48"
          color="warning"
          variant="tonal"
          class="mb-4"
        >
          <VIcon
            icon="tabler-alert-circle"
            size="24"
          />
        </VAvatar>

        <!-- Title -->
        <h3 class="text-h6 font-weight-semibold mb-4">
          {{ t('common.notification') }}
        </h3>

        <!-- Message -->
        <p class="text-body-1 mb-2 font-weight-medium">
          {{ t('auth.force_logout_message') }}
        </p>
      </VCardText>

      <!-- Actions -->
      <VCardActions class="justify-center pb-6">
        <VBtn
          color="primary"
          class="px-6"
          @click="handleOk"
        >
          {{ t('common.ok') }}
        </VBtn>
      </VCardActions>

      <!-- Countdown -->
      <VCardText class="pt-0 pb-6">
        <p class="text-caption text-medium-emphasis">
          {{ t('common.auto_close_in') }}
          <span class="font-weight-bold">{{ countdown }}</span>
          {{ t('common.seconds') }}
        </p>
      </VCardText>
    </VCard>
  </VDialog>
</template>
